import {
	Autocomplete,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../../api/api';
import { readCachedStructure } from '../../../hooks/useConfigStorage';
import { getApiVersion } from '../../../redux/apiVersion';
import {
	type PageNameType,
	createClientConfigurationStart,
	getClientConfigurationsStart,
	updateClientConfigurationStart,
} from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import type { StructureType } from '../../../types/types';
import {
	SMART_SEARCH_FILTER_OPERATORS,
	SMART_SEARCH_TARGET_PAGES,
	type SmartSearchMatchType,
	type SmartSearchRule,
	buildSmartSearchConfigPayload,
	findSmartSearchConfigId,
	getSmartSearchRules,
	isValidSmartSearchRule,
	loadCustomSmartSearchRules,
	removeCustomSmartSearchRule,
	syncSmartSearchRulesFromConfigurations,
	upsertCustomSmartSearchRule,
} from '../../../utils/smartSearch';

const MATCH_TYPES: SmartSearchMatchType[] = [
	'startsWith',
	'includes',
	'equals',
	'regex',
];

// Field types a text search can filter on.
const FILTERABLE_TYPES = ['string', 'int32', 'int16', 'double', 'decimal'];

type FieldOption = { value: string; label: string };

// Top-level filterable fields plus one level of model children
// (e.g. Contract.Number), labelled with the backend translations.
const structureToFieldOptions = (structure: StructureType[]): FieldOption[] => {
	const options: FieldOption[] = [];
	structure.forEach((item) => {
		if (FILTERABLE_TYPES.includes(item.Type)) {
			options.push({ value: item.Name, label: item.Translation || item.Name });
		}
		if (item.Type === 'model' && Array.isArray(item.InnerStructure)) {
			item.InnerStructure.forEach((child) => {
				if (FILTERABLE_TYPES.includes(child.Type)) {
					options.push({
						value: `${item.Name}.${child.Name}`,
						label: `${item.Translation || item.Name} – ${child.Translation || child.Name}`,
					});
				}
			});
		}
	});
	return options;
};

const emptyDraft = (): SmartSearchRule => ({
	id: '',
	name: '',
	priority: 50,
	matchType: 'startsWith',
	matchValue: '',
	route: SMART_SEARCH_TARGET_PAGES[0].route,
	filterField: '',
	filterOperator: 'contains',
	isBuiltIn: false,
});

const SmartSearchRulesModal = ({
	isOpen,
	handleClose,
	initialDraft,
	onSaved,
}: {
	isOpen: boolean;
	handleClose: () => void;
	// Prefill the rule form (e.g. "map this search text to a grid").
	initialDraft?: Partial<SmartSearchRule>;
	onSaved?: (rule: SmartSearchRule) => void;
}) => {
	const { t, i18n } = useTranslation();
	const dispatch = useAppDispatch();
	const clientConfigurations = useAppSelector(
		(state) => state.clientConfiguration.clientConfigurations,
	);

	const [rules, setRules] = useState<SmartSearchRule[]>(() =>
		getSmartSearchRules(),
	);
	const [draft, setDraft] = useState<SmartSearchRule | null>(() =>
		initialDraft ? { ...emptyDraft(), ...initialDraft } : null,
	);
	const [fieldOptions, setFieldOptions] = useState<FieldOption[]>([]);
	const [fieldsLoading, setFieldsLoading] = useState(false);
	const fieldOptionsCache = useRef<Record<string, FieldOption[]>>({});

	const draftRoute = draft?.route;

	// Load the selected grid's structure so the user can pick a field from a
	// list instead of typing it. LocalStorage structure cache first (same as
	// the table pages), then the structure endpoint.
	useEffect(() => {
		if (!draftRoute) return;
		const page = SMART_SEARCH_TARGET_PAGES.find(
			(targetPage) => targetPage.route === draftRoute,
		);
		if (!page) {
			setFieldOptions([]);
			return;
		}
		const table = page.entityKey;
		const cachedOptions = fieldOptionsCache.current[table];
		if (cachedOptions) {
			setFieldOptions(cachedOptions);
			return;
		}

		let cancelled = false;
		const load = async () => {
			setFieldsLoading(true);
			try {
				const baseCap = table.charAt(0).toUpperCase() + table.slice(1);
				// Shares the saga's structure cache (full page-name key, lang+TTL
				// stamped) — the old read under `baseCap` never hit.
				const stored = readCachedStructure(table, i18n.language || 'lt');
				let structure: StructureType[];
				if (Array.isArray(stored) && stored.length > 0) {
					structure = stored as StructureType[];
				} else {
					const version = getApiVersion(
						table as PageNameType,
						'getTableStructure',
					);
					const response = await api.get(
						`/${baseCap}/v${version}/structure?lang=${i18n.language || 'lt'}`,
					);
					structure = Array.isArray(response?.data) ? response.data : [];
				}
				const options = structureToFieldOptions(structure);
				fieldOptionsCache.current[table] = options;
				if (!cancelled) setFieldOptions(options);
			} catch {
				// Structure unavailable — the field stays editable as free text.
				if (!cancelled) setFieldOptions([]);
			} finally {
				if (!cancelled) setFieldsLoading(false);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [draftRoute, i18n.language]);

	const refresh = () => setRules(getSmartSearchRules());

	// Read rules from the backend (POST /ClientConfiguration/v1) when the
	// dialog opens, then mirror them into the local cache the matcher uses.
	useEffect(() => {
		if (isOpen) dispatch(getClientConfigurationsStart({}));
	}, [isOpen, dispatch]);

	useEffect(() => {
		if (syncSmartSearchRulesFromConfigurations(clientConfigurations)) {
			refresh();
		}
	}, [clientConfigurations]);

	// Persist the full rules set to the backend: create the config row on first
	// save, update it afterwards. LocalStorage is already updated by the caller.
	const persistRules = () => {
		const allRules = loadCustomSmartSearchRules();
		const existingId = findSmartSearchConfigId(clientConfigurations);
		const payload = buildSmartSearchConfigPayload(existingId || '-1', allRules);
		if (existingId) {
			dispatch(updateClientConfigurationStart(payload));
		} else {
			dispatch(createClientConfigurationStart(payload));
		}
	};

	const setDraftField = <K extends keyof SmartSearchRule>(
		key: K,
		value: SmartSearchRule[K],
	) => {
		setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
	};

	const saveDraft = () => {
		if (!draft) return;
		const rule: SmartSearchRule = {
			...draft,
			id: draft.id || `custom-${Date.now()}`,
			priority: Number(draft.priority) || 0,
		};
		if (!isValidSmartSearchRule(rule)) return;
		upsertCustomSmartSearchRule(rule);
		persistRules();
		setDraft(null);
		refresh();
		onSaved?.(rule);
	};

	const deleteRule = (ruleId: string) => {
		removeCustomSmartSearchRule(ruleId);
		persistRules();
		refresh();
	};

	const draftValid =
		draft &&
		isValidSmartSearchRule({
			...draft,
			id: draft.id || 'pending',
			priority: Number(draft.priority) || 0,
		});

	const pageLabel = (route: string) => {
		const page = SMART_SEARCH_TARGET_PAGES.find((p) => p.route === route);
		return page ? t(`entities.${page.entityKey}`) : route;
	};

	// Built-in default rules resolve their display name through the locale
	// files (keyed by rule id); user rules show the name they were saved with.
	const ruleLabel = (rule: SmartSearchRule) =>
		rule.isBuiltIn
			? t(`global.defaultRuleNames.${rule.id}`, { defaultValue: rule.name })
			: rule.name;

	return (
		<Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{t('global.smartSearchRules')}</DialogTitle>
			<DialogContent>
				<Stack spacing={1} divider={<Divider flexItem />}>
					{rules.map((rule) => (
						<Box
							key={rule.id}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								py: 0.5,
							}}
						>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography variant="body2" fontWeight={600} noWrap>
									{ruleLabel(rule)}
									{rule.isBuiltIn && (
										<Chip
											label={t('global.builtIn')}
											size="small"
											sx={{ ml: 1 }}
										/>
									)}
								</Typography>
								<Typography variant="caption" color="text.secondary" noWrap>
									{t(`global.matchTypes.${rule.matchType}`)}: "{rule.matchValue}
									" → {pageLabel(rule.route)} ({rule.filterField}{' '}
									{t(`global.operators.${rule.filterOperator || 'contains'}`)}),{' '}
									{t('global.priority')}: {rule.priority}
								</Typography>
							</Box>
							{!rule.isBuiltIn && (
								<>
									<Button size="small" onClick={() => setDraft({ ...rule })}>
										{t('global.edit')}
									</Button>
									<Button
										size="small"
										color="error"
										onClick={() => deleteRule(rule.id)}
									>
										{t('global.delete')}
									</Button>
								</>
							)}
						</Box>
					))}
				</Stack>

				{draft ? (
					<Stack spacing={2} sx={{ mt: 3 }}>
						<TextField
							size="small"
							label={t('global.ruleName')}
							value={draft.name}
							onChange={(e) => setDraftField('name', e.target.value)}
							fullWidth
						/>
						<Box sx={{ display: 'flex', gap: 2 }}>
							<TextField
								size="small"
								select
								label={t('global.matchType')}
								value={draft.matchType}
								onChange={(e) =>
									setDraftField(
										'matchType',
										e.target.value as SmartSearchMatchType,
									)
								}
								sx={{ flex: 1 }}
							>
								{MATCH_TYPES.map((type) => (
									<MenuItem key={type} value={type}>
										{t(`global.matchTypes.${type}`)}
									</MenuItem>
								))}
							</TextField>
							<TextField
								size="small"
								label={t('global.matchValue')}
								value={draft.matchValue}
								onChange={(e) => setDraftField('matchValue', e.target.value)}
								sx={{ flex: 1 }}
							/>
						</Box>
						<Box sx={{ display: 'flex', gap: 2 }}>
							<TextField
								size="small"
								select
								label={t('global.targetPage')}
								value={draft.route}
								onChange={(e) => setDraftField('route', e.target.value)}
								sx={{ flex: 1 }}
							>
								{SMART_SEARCH_TARGET_PAGES.map((page) => (
									<MenuItem key={page.route} value={page.route}>
										{t(`entities.${page.entityKey}`)}
									</MenuItem>
								))}
							</TextField>
							<Autocomplete
								size="small"
								freeSolo
								loading={fieldsLoading}
								options={fieldOptions}
								getOptionLabel={(option) =>
									typeof option === 'string' ? option : option.label
								}
								value={
									fieldOptions.find(
										(option) => option.value === draft.filterField,
									) ||
									draft.filterField ||
									''
								}
								onChange={(_, option) =>
									setDraftField(
										'filterField',
										typeof option === 'string' ? option : (option?.value ?? ''),
									)
								}
								onInputChange={(_, value, reason) => {
									// Allow typing a custom field when the list misses it.
									if (reason === 'input') setDraftField('filterField', value);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										label={t('global.filterField')}
										placeholder="Number"
									/>
								)}
								sx={{ flex: 1 }}
							/>
						</Box>
						<Box sx={{ display: 'flex', gap: 2 }}>
							<TextField
								size="small"
								select
								label={t('global.operator')}
								value={draft.filterOperator || 'contains'}
								onChange={(e) =>
									setDraftField(
										'filterOperator',
										e.target.value as SmartSearchRule['filterOperator'],
									)
								}
								sx={{ flex: 1 }}
							>
								{SMART_SEARCH_FILTER_OPERATORS.map((operator) => (
									<MenuItem key={operator} value={operator}>
										{t(`global.operators.${operator}`)}
									</MenuItem>
								))}
							</TextField>
							<TextField
								size="small"
								type="number"
								label={t('global.priority')}
								value={draft.priority}
								onChange={(e) =>
									setDraftField('priority', Number(e.target.value))
								}
								sx={{ flex: 1 }}
							/>
						</Box>
						<Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
							<Button onClick={() => setDraft(null)}>
								{t('global.cancel')}
							</Button>
							<Button
								variant="contained"
								disabled={!draftValid}
								onClick={saveDraft}
							>
								{t('global.save')}
							</Button>
						</Box>
					</Stack>
				) : (
					<Box sx={{ mt: 2 }}>
						<Button variant="outlined" onClick={() => setDraft(emptyDraft())}>
							{t('global.addRule')}
						</Button>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>{t('global.close')}</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SmartSearchRulesModal;
